import React, { useEffect, useState } from "react";
import Navbar from "./components/Ui/Navbar";
import { axiosInstance } from "./Api/config";
import Home from "./components/Ui/Home";
import About from "./components/Ui/About";
import Services from "./components/Ui/Services";
import HappyCostomer from "./components/Ui/HappyCostomer";
import Contact from "./components/Ui/Contact";
import Footer from "./components/Ui/Footer";
import TestRideModal from "./components/TestRideModal";
import { AuthProvider, useAuth } from "./components/Admin/context/AuthContext";
import AdminLayout from "./components/Admin/layout/AdminLayout";
import Dashboard from "./components/Admin/pages/Dashboard";
import Login from "./components/Admin/pages/Login";
import Motorcycles from "./components/Admin/pages/Motorcycles";
import HomeSlides from "./components/Admin/pages/HomeSlides";
import Testimonials from "./components/Admin/pages/Testimonials";
import Bookings from "./components/Admin/pages/Bookings";
import Inquiries from "./components/Admin/pages/Inquiries";
import BikeModels from "./components/Admin/pages/BikeModels";


// Global dark theme styles - Golden Theme
const globalStyles = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    background: #050505;
    color: #F5F5F5;
    overflow-x: hidden;
    // cursor: none;
  }

  /* Custom cursor */
  .cursor-dot {
    position: fixed; width: 10px; height: 10px;
    background: #D4AF37; border-radius: 50%;
    pointer-events: none; z-index: 9999;
    transform: translate(-50%, -50%);
    transition: transform 0.08s;
    mix-blend-mode: difference;
  }
  

  /* Scrollbar */
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: #050505; }
  ::-webkit-scrollbar-thumb { background: #D4AF37; border-radius: 2px; }
`;

const navigateTo = (path) => {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new Event("app:navigate"));
};

function useCurrentPath() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const updatePath = () => setPath(window.location.pathname);

    window.addEventListener("popstate", updatePath);
    window.addEventListener("app:navigate", updatePath);

    return () => {
      window.removeEventListener("popstate", updatePath);
      window.removeEventListener("app:navigate", updatePath);
    };
  }, []);

  return path;
}

function PublicSite() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  
  const handleBookTestRide = async (formData) => {
    try {
      let motorcycleId = null;
      try {
        const bikesRes = await axiosInstance.get("/motorcycles/active");
        const matchedBike = (bikesRes.data || []).find(
          (b) => b.name.toLowerCase() === formData.bikeModel.toLowerCase()
        );
        if (matchedBike) {
          motorcycleId = matchedBike.id;
        }
      } catch (err) {
        console.error("Could not fetch motorcycles for matching:", err);
      }

      const combinedMessage = [
        formData.message,
        formData.bikeModel ? `Model: ${formData.bikeModel}` : "",
        formData.preferredDate ? `Date: ${formData.preferredDate}` : "",
        formData.preferredTime ? `Time: ${formData.preferredTime}` : ""
      ]
        .filter(Boolean)
        .join("\n");

      await axiosInstance.post("/bike-bookings", {
        motorcycle_id: motorcycleId,
        name: formData.name,
        phone: formData.phone,
        email: formData.email || "",
        city: "",
        message: combinedMessage,
      });
    } catch (err) {
      console.error("Error saving test ride booking:", err);
      alert("Failed to submit test ride booking. Please try again.");
    }
  };

  return (
    <>
      <style>{globalStyles}</style>

      {/* Custom cursor */}
      <div className="cursor-dot" id="cursorDot" />
      <div className="cursor-ring" id="cursorRing" />

      <script dangerouslySetInnerHTML={{
        __html: `
          const dot = document.getElementById('cursorDot');
          const ring = document.getElementById('cursorRing');
          if (dot && ring) {
            document.addEventListener('mousemove', e => {
              dot.style.left = e.clientX + 'px';
              dot.style.top = e.clientY + 'px';
              setTimeout(() => {
                ring.style.left = e.clientX + 'px';
                ring.style.top = e.clientY + 'px';
              }, 80);
            });
          }
        `
      }} />

      <TestRideModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleBookTestRide}
      />

      <Navbar onOpenModal={handleOpenModal} />
      <main>
        <Home onOpenModal={handleOpenModal} />
        <About onOpenModal={handleOpenModal} />
        <Services onOpenModal={handleOpenModal} />
        <HappyCostomer onOpenModal={handleOpenModal} />
        <Contact onOpenModal={handleOpenModal} />
        <Footer onOpenModal={handleOpenModal} />
      </main>
    </>
  );
}

function AdminApp({ currentPath }) {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!currentPath.startsWith("/admin")) {
      return;
    }

    if (!isAuthenticated && currentPath !== "/admin/login") {
      navigateTo("/admin/login");
      return;
    }

    if (isAuthenticated && (currentPath === "/admin" || currentPath === "/admin/login")) {
      navigateTo("/admin/dashboard");
    }
  }, [currentPath, isAuthenticated]);

  if (!isAuthenticated) {
    return <Login onLoginSuccess={() => navigateTo("/admin/dashboard")} />;
  }

  const renderPage = () => {
    switch (currentPath) {
      case "/admin/dashboard":
        return <Dashboard />;
      case "/admin/motorcycles":
        return <Motorcycles />;
      case "/admin/home-slides":
        return <HomeSlides />;
      case "/admin/testimonials":
        return <Testimonials />;
      case "/admin/bookings":
        return <Bookings />;
      case "/admin/inquiries":
        return <Inquiries />;
      case "/admin/settings/bike-models":
        return <BikeModels />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <AdminLayout currentPath={currentPath} navigate={navigateTo}>
      {renderPage()}
    </AdminLayout>
  );
}

function App() {
  const currentPath = useCurrentPath();

  if (currentPath.startsWith("/admin")) {
    return (
      <AuthProvider>
        <AdminApp currentPath={currentPath} />
      </AuthProvider>
    );
  }

  return <PublicSite />;
}

export default App;
